/**
 * AI Module - Configuration
 *
 * Centralized configuration for LLM models, rate limits, and settings.
 * All model-specific settings should be defined here.
 */

import type { LLMConfig, RateLimitConfig } from './types';

// ============================================
// Model Configurations
// ============================================

export const MODEL_CONFIGS: Record<string, LLMConfig> = {
  // Clarifier model — fast, cheap, simple decisions
  'claude-3-5-haiku-20241022': {
    maxTokens: 4096,
    costPer1kInput: 0.0008,
    costPer1kOutput: 0.004,
    timeoutMs: 30000,
    useCase: 'clarifier',
  },

  // Planner model — primary journey generation (Claude 3.5 Sonnet for speed)
  'claude-3-5-sonnet-20241022': {
    maxTokens: 5000,
    costPer1kInput: 0.003,
    costPer1kOutput: 0.015,
    timeoutMs: 50000, // 50s internal timeout (under 60s Vercel Hobby limit)
    useCase: 'planner',
  },

  // Opus — reserved for future complex reasoning tasks
  'claude-opus-4-5-20251101': {
    maxTokens: 16384,
    costPer1kInput: 0.015,
    costPer1kOutput: 0.075,
    timeoutMs: 120000,
    useCase: 'complex_reasoning',
  },
};

// ============================================
// Model Selection for Journey Feature
// ============================================

export const JOURNEY_MODELS = {
  clarifier: 'claude-3-5-haiku-20241022',
  planner: 'claude-3-5-sonnet-20241022', // Faster than newer Sonnet, same quality
} as const;

// ============================================
// Rate Limiting Configuration
// Note: Actual rate limits are enforced server-side in api/config.ts
// This is kept for backward compatibility with type exports
// ============================================

export const RATE_LIMITS: Record<string, RateLimitConfig> = {
  weekly: { max: 3, windowMs: 7 * 24 * 60 * 60 * 1000 }, // 3 per week (email-based)
};

// ============================================
// Retry Configuration
// ============================================

export const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

// ============================================
// Telemetry Configuration
// ============================================

// Environment detection - works in both Vite (frontend) and Node.js (serverless)
const getEnvironment = () => {
  // Node.js environment (Vercel serverless functions)
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return {
      isProduction: process.env.NODE_ENV === 'production',
      isDevelopment: process.env.NODE_ENV === 'development',
    };
  }
  // Vite environment (frontend)
  try {
    // @ts-ignore - import.meta.env exists in Vite
    const env = import.meta?.env;
    if (env) {
      return {
        isProduction: env.PROD ?? false,
        isDevelopment: env.DEV ?? true,
      };
    }
  } catch {
    // Fallback
  }
  return { isProduction: false, isDevelopment: true };
};

const { isProduction, isDevelopment } = getEnvironment();

export const TELEMETRY_CONFIG = {
  enabled: isProduction,
  flushIntervalMs: 30000,
  maxBufferSize: 50,
  logInDev: true,
};

// ============================================
// Feature Flags
// ============================================

export const AI_FEATURE_FLAGS = {
  // Enable/disable clarifier pass
  enableClarifier: true,

  // Enable/disable hallucination detection
  enableHallucinationDetection: true,

  // Enable/disable fallback journey on failure
  enableFallback: true,

  // Enable/disable caching
  enableCaching: true,

  // Log LLM calls in development
  logLLMCalls: isDevelopment,
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get configuration for a specific model
 */
export function getModelConfig(modelId: string): LLMConfig | undefined {
  return MODEL_CONFIGS[modelId];
}

/**
 * Get the model ID for a specific use case
 */
export function getModelForUseCase(useCase: 'clarifier' | 'planner'): string {
  return JOURNEY_MODELS[useCase];
}

/**
 * Calculate cost for a given token usage
 */
export function calculateCost(
  modelId: string,
  inputTokens: number,
  outputTokens: number
): number {
  const config = MODEL_CONFIGS[modelId];
  if (!config) return 0;

  return (
    (inputTokens / 1000) * config.costPer1kInput +
    (outputTokens / 1000) * config.costPer1kOutput
  );
}

/**
 * Check if a model is configured
 */
export function isValidModel(modelId: string): boolean {
  return modelId in MODEL_CONFIGS;
}

// ============================================
// Storage Keys
// ============================================

export const STORAGE_KEYS = {
  journey: 'yantra_journey',
  journeyHistory: 'yantra_journey_history',
} as const;

// ============================================
// API Endpoints
// ============================================

export const API_ENDPOINTS = {
  journey: '/api/journey',
} as const;
